import ActionSuccessToast from '../components/ActionSuccessToast'
import { useState, useEffect, useCallback } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { useSettings } from '../hooks/useSettings'
import { useAccountNicknames } from '../hooks/useAccountNicknames'
import { fmt, calcMaxLoss, calcTrancheSizing } from '../lib/calculations'
import { usePrivacy } from '../hooks/usePrivacy'
import {
  computePriorClosePct,
  getAccountCash,
  getAccountValue,
  summarizeDayChange,
} from '../lib/portfolioAnalytics'

function fmtEquity(val) {
  if (val == null) return '—'
  const n = Number(val)
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}k`
  return `$${n.toFixed(2)}`
}

function StatCard({ label, value, sub, accent }) {
  return (
    <div className="bg-canvas border border-border rounded-xl p-5">
      <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-2">{label}</div>
      <div className={`font-mono text-2xl font-semibold ${accent || 'text-ink'}`}>{value}</div>
      {sub && <div className="font-mono text-xs text-inkMuted mt-1">{sub}</div>}
    </div>
  )
}

export default function AccountDetail() {
  const { accountNumber: paramSlug } = useParams()
  const { user } = useAuth()
  const { settings: sysSettings } = useSettings()
  const { getNickname, getAccountSettings, updateAccountSettings } = useAccountNicknames()
  const { privacyMode } = usePrivacy()
  const mask = (val) => privacyMode ? '•••••' : val

  const [account, setAccount] = useState(null)
  const [positions, setPositions] = useState([])
  const [loading, setLoading] = useState(true)

  // Local form state for settings
  const [nickname, setNicknameLocal] = useState('')
  const [useCustomAlloc, setUseCustomAlloc] = useState(false)
  const [customAlloc, setCustomAlloc] = useState('')
  const [useCustomRisk, setUseCustomRisk] = useState(false)
  const [customRisk, setCustomRisk] = useState('')
  const [notes, setNotes] = useState('')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  const loadData = useCallback(async () => {
    if (!user) return
    if (!account) setLoading(true)

    const [{ data: accts }, { data: pos }] = await Promise.all([
      supabase
        .from('schwab_accounts')
        .select('account_number, nick_name, account_type, equity')
        .eq('user_id', user.id),
      supabase
        .from('schwab_positions')
        .select('account_number, symbol, instrument_type, quantity, average_price, market_value, current_day_pnl, prev_close_price, raw, status, cost_basis_long, cost_basis_short, unrealized_pnl_long, unrealized_pnl_short, cost_basis_source, currency, contract_multiplier')
        .eq('user_id', user.id)
        .or('status.eq.open,status.is.null'),
    ])

    // Find account by last 4 digits
    const match = (accts || []).find(a => a.account_number.endsWith(paramSlug))
    setAccount(match || null)

    if (match) {
      setPositions((pos || []).filter(p => p.account_number === match.account_number))
    }

    setLoading(false)
  }, [user, paramSlug])

  useEffect(() => { loadData() }, [loadData])

  // Sync form state when account settings load
  useEffect(() => {
    if (!account) return
    const s = getAccountSettings(account.account_number)
    setNicknameLocal(s.nickname || '')
    setUseCustomAlloc(s.custom_alloc_pct != null)
    setCustomAlloc(s.custom_alloc_pct != null ? String(s.custom_alloc_pct) : '')
    setUseCustomRisk(s.custom_risk_pct != null)
    setCustomRisk(s.custom_risk_pct != null ? String(s.custom_risk_pct) : '')
    setNotes(s.notes || '')
  }, [account, getAccountSettings])

  const handleSave = async () => {
    if (!account) return
    setSaving(true)
    setSaved(false)
    await updateAccountSettings(account.account_number, {
      nickname: nickname.trim() || null,
      custom_alloc_pct: useCustomAlloc && customAlloc ? parseFloat(customAlloc) : null,
      custom_risk_pct: useCustomRisk && customRisk ? parseFloat(customRisk) : null,
      notes: notes.trim() || null,
    })
    setSaving(false)
    setSaved(true)
  }

  if (loading) {
    return <div className="font-mono text-xs text-inkMuted animate-pulse">Loading…</div>
  }

  if (!account) {
    return (
      <div className="space-y-4">
        <Link to="/accounts/summary" className="inline-flex items-center gap-1.5 font-mono text-xs text-inkMuted hover:text-ink transition-colors">
          <ArrowLeft size={14} />
          Back to Accounts
        </Link>
        <div className="bg-canvas border border-border rounded-xl px-6 py-16 text-center">
          <div className="font-mono text-xs text-inkMuted">Account not found</div>
        </div>
      </div>
    )
  }

  // Derived values
  const equity = getAccountValue(account)
  const cash = getAccountCash(account)
  const { totalDayChange: totalDayPnl } = summarizeDayChange([account], positions)
  const dayPct = computePriorClosePct(totalDayPnl, equity)
  const dayColor = totalDayPnl > 0 ? 'text-forest' : totalDayPnl < 0 ? 'text-ember' : 'text-ink'

  // Sizing — use custom overrides or system defaults
  const acctSettings = getAccountSettings(account.account_number)
  const allocPct = acctSettings.custom_alloc_pct ?? sysSettings.default_alloc_pct
  const riskPct = acctSettings.custom_risk_pct ?? sysSettings.default_risk_pct
  const perTranche = calcTrancheSizing(equity, allocPct)
  const maxLoss = calcMaxLoss(equity, allocPct, riskPct)

  const displayName = getNickname(account.account_number)
  const maskedNum = `···${account.account_number.slice(-4)}`

  return (
    <div className="space-y-6">

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div>
        <Link to="/accounts/summary" className="inline-flex items-center gap-1.5 font-mono text-xs text-inkMuted hover:text-ink transition-colors mb-4">
          <ArrowLeft size={14} />
          Back to Accounts
        </Link>
        <div className="flex items-center gap-3">
          <div>
            <h1 className="font-black text-2xl uppercase tracking-wide text-ink">{displayName}</h1>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="font-mono text-xs text-inkMuted">{maskedNum}</span>
              {account.account_type && (
                <span className="font-mono text-[10px] px-2 py-0.5 rounded-full bg-canvas2 border border-border text-inkMuted uppercase tracking-wider">
                  {account.account_type}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Section 1: Overview ─────────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard label="Equity" value={mask(fmtEquity(equity))} />
        <StatCard label="Cash" value={mask(fmtEquity(cash))} />
        <StatCard label="Positions" value={positions.length} />
        <StatCard
          label="Day Change $"
          value={mask((totalDayPnl >= 0 ? '+' : '') + fmt(totalDayPnl))}
          accent={dayColor}
        />
        <StatCard
          label="Day Change %"
          value={mask((dayPct >= 0 ? '+' : '') + dayPct.toFixed(2) + '%')}
          accent={dayColor}
        />
      </div>

      {/* ── Section 2: Account Settings ─────────────────────────────────── */}
      <div className="bg-canvas border border-border rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-border">
          <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-0.5">Preferences</div>
          <h2 className="font-black text-base uppercase tracking-wide text-ink">Account Settings</h2>
        </div>
        <div className="px-6 py-5 space-y-5">

          {/* Nickname */}
          <div>
            <label className="label block mb-1.5">Nickname</label>
            <input
              type="text"
              value={nickname}
              onChange={e => setNicknameLocal(e.target.value)}
              placeholder={maskedNum}
              className="input-field w-full max-w-sm"
            />
            <div className="font-mono text-[10px] text-inkMuted mt-1">
              Displayed across the app instead of the masked account number
            </div>
          </div>

          {/* Custom Allocation */}
          <div>
            <label className="label block mb-1.5">Allocation %</label>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={useCustomAlloc}
                  onChange={e => {
                    setUseCustomAlloc(e.target.checked)
                    if (!e.target.checked) setCustomAlloc('')
                  }}
                  className="accent-ink"
                />
                <span className="font-mono text-xs text-inkMuted">Override system default</span>
              </label>
            </div>
            {useCustomAlloc ? (
              <div className="mt-2 flex items-center gap-2">
                <input
                  type="number"
                  step="0.5"
                  min="1"
                  max="100"
                  value={customAlloc}
                  onChange={e => setCustomAlloc(e.target.value)}
                  className="input-field w-24"
                />
                <span className="font-mono text-xs text-inkMuted">%</span>
              </div>
            ) : (
              <div className="font-mono text-xs text-inkMuted mt-1.5">
                Using system default: <strong className="text-ink">{sysSettings.default_alloc_pct}%</strong>
              </div>
            )}
          </div>

          {/* Custom Risk */}
          <div>
            <label className="label block mb-1.5">Risk Per Trade %</label>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={useCustomRisk}
                  onChange={e => {
                    setUseCustomRisk(e.target.checked)
                    if (!e.target.checked) setCustomRisk('')
                  }}
                  className="accent-ink"
                />
                <span className="font-mono text-xs text-inkMuted">Override system default</span>
              </label>
            </div>
            {useCustomRisk ? (
              <div className="mt-2 flex items-center gap-2">
                <input
                  type="number"
                  step="0.25"
                  min="0.5"
                  max="25"
                  value={customRisk}
                  onChange={e => setCustomRisk(e.target.value)}
                  className="input-field w-24"
                />
                <span className="font-mono text-xs text-inkMuted">%</span>
              </div>
            ) : (
              <div className="font-mono text-xs text-inkMuted mt-1.5">
                Using system default: <strong className="text-ink">{sysSettings.default_risk_pct}%</strong>
              </div>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="label block mb-1.5">Notes</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="e.g., Karen's retirement account — conservative only"
              rows={3}
              className="input-field w-full max-w-lg resize-y"
            />
          </div>

          {/* Save */}
          <div className="flex items-center gap-3 pt-1">
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn-primary text-xs"
            >
              {saving ? 'Saving…' : 'Save Settings'}
            </button>
            {saved && (
              <ActionSuccessToast message="Account settings saved" onDismiss={() => setSaved(false)} />
            )}
          </div>
        </div>
      </div>

      {/* ── Section 3: Positions ────────────────────────────────────────── */}
      <div className="bg-canvas border border-border rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-border">
          <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-0.5">This Account</div>
          <h2 className="font-black text-base uppercase tracking-wide text-ink">
            {positions.length} Position{positions.length !== 1 ? 's' : ''}
          </h2>
        </div>
        {positions.length === 0 ? (
          <div className="px-6 py-12 text-center font-mono text-xs text-inkMuted">
            No positions in this account
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-canvas2">
                  {['Symbol', 'Type', 'Qty', 'Avg Cost', 'Mkt Value', 'Day P&L'].map(h => (
                    <th key={h} className="px-4 py-3 text-left font-mono text-[10px] tracking-widest uppercase text-inkMuted">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {positions.map((pos, i) => {
                  const pnl = pos.current_day_pnl
                  const pnlColor = pnl > 0 ? 'text-forest' : pnl < 0 ? 'text-ember' : 'text-inkMuted'
                  return (
                    <tr key={`${pos.symbol}-${i}`} className={`border-b border-border last:border-0 ${i % 2 === 0 ? '' : 'bg-canvas2/30'}`}>
                      <td className="px-4 py-3 font-mono text-sm font-semibold text-ink">{pos.symbol}</td>
                      <td className="px-4 py-3 font-mono text-[10px] text-border uppercase tracking-wide">{pos.instrument_type || '—'}</td>
                      <td className="px-4 py-3 font-mono text-xs text-inkMuted">{mask(pos.quantity ?? '—')}</td>
                      <td className="px-4 py-3 font-mono text-xs text-inkMuted">
                        {mask(pos.average_price != null ? `$${Number(pos.average_price).toFixed(2)}` : '—')}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs font-semibold text-ink">{mask(fmtEquity(pos.market_value))}</td>
                      <td className={`px-4 py-3 font-mono text-xs font-semibold ${pnlColor}`}>
                        {mask(pnl != null ? (pnl >= 0 ? '+' : '') + fmt(pnl) : '—')}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Section 4: Account Sizing ───────────────────────────────────── */}
      {equity > 0 && (
        <div className="bg-canvas border border-border rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-border">
            <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-0.5">
              {acctSettings.custom_alloc_pct != null || acctSettings.custom_risk_pct != null
                ? 'Custom Overrides Active'
                : 'Using System Defaults'}
              {' · '}{allocPct}% Alloc · {riskPct}% Risk
            </div>
            <h2 className="font-black text-base uppercase tracking-wide text-ink">Account Sizing</h2>
          </div>
          <div className="px-6 py-5 grid grid-cols-2 md:grid-cols-5 gap-4">
            <div>
              <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-1">Per Tranche</div>
              <div className="font-mono text-xl font-semibold text-ink">{mask(fmt(perTranche))}</div>
            </div>
            <div>
              <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-1">Max $ Loss</div>
              <div className="font-mono text-xl font-semibold text-ember">{mask(fmt(maxLoss))}</div>
            </div>
            <div>
              <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-1">Full Pos 10%</div>
              <div className="font-mono text-xl font-semibold text-forest">{mask(fmtEquity(equity * 0.10))}</div>
            </div>
            <div>
              <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-1">Full Pos 15%</div>
              <div className="font-mono text-xl font-semibold text-forest">{mask(fmtEquity(equity * 0.15))}</div>
            </div>
            <div>
              <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-1">Full Pos 25%</div>
              <div className="font-mono text-xl font-semibold text-forest">{mask(fmtEquity(equity * 0.25))}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
